import {
  LazyMotion,
  MotionConfig,
  domAnimation,
} from "framer-motion";
import type {
  LoaderFunctionArgs,
  LinksFunction,
} from "@remix-run/cloudflare";
import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import {
  Form,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRevalidator,
  useRouteError,
} from "@remix-run/react";
import stylesheet from "./tailwind.css?url";
import "~/styles/app.scss";
import {
  createSbClientBrowser,
  createSbClientSSR,
  dbConfig,
  envURL,
  stripeKeyEnv,
} from "./utils/createSbClientSSR";
import AppHeader from "./components/Common/AppHeader";
import { useEffect } from "react";
import type { EnvBrowser } from "./utils/env";
import { environment } from "./utils/env";
import { generateFileURLBasePath } from "./utils/file/generateFileURL";
import { UserService } from "./database/user.service";
import Stripe from "stripe";
import { Toaster } from "react-hot-toast";
import { StripeService } from "./database/stripe.service";
import { withSentry } from "@sentry/remix";
import AppFooter from "./components/Common/AppFooter";
import { NotificationsService } from "./database/notifications.service";
import { easeUse } from "./utils/easingUse";
import clsx from "clsx";
import ProgressBar from "./components/Common/ProgressBar";

/* 
headerのユーザー名表示などはこの辺で取得してcontextで回したらいいかな
*/

/* 
login後の再フェッチについて考える必要がある
*/

export const loader = async ({
  request,
  context,
}: LoaderFunctionArgs) => {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const userResponse =
    await supabase.auth.getUser();
  const env = environment(context);
  const browsorEnv: EnvBrowser = {
    R2_PUBLIC_URL: envURL(env, env.DB_ENV)
      .R2_PUBLIC_URL,
    WORKER_URL: envURL(env, env.DB_ENV)
      .WORKER_URL,
    SITE_URL: envURL(env, env.DB_ENV).SITE_URL,
    DB_ENV: environment(context).DB_ENV,
    SB_URL: dbConfig(env, env.DB_ENV).url,
    SB_ANON_KEY: dbConfig(env, env.DB_ENV)
      .anonKey,
    STRIPE_PUBLIC_KEY: stripeKeyEnv(
      env,
      env.DB_ENV,
    ).STRIPE_PUBLIC_KEY,
  };

  const base = {
    ok: true,
    user: userResponse?.data?.user,
    userData: null,
    notificationData: null,

    ENV: browsorEnv,
    basePath: generateFileURLBasePath({
      ...browsorEnv,
    }),
    apiURL: envURL(env, env.DB_ENV).WORKER_URL,
    subscription_active: false,
    paymentIssue: null,
  };
  if (userResponse?.data?.user?.id) {
    try {
      const userData =
        await UserService.getUserMine(supabase);
      if (userData.password_reset_required) {
        const isPasswordReset =
          request.url.indexOf(
            "update-password",
          ) !== -1;
        if (!isPasswordReset) {
          return redirect(
            "/update-password?invite=true",
          );
        }
      }
      const notificationData =
        await NotificationsService.getNotifications(
          supabase,
          userData.id,
          {
            limit: 30,
          },
        );

      const uid = userResponse?.data?.user?.id;
      const url = new URL(request.url);
      const paymentIntent = url.searchParams.get(
        "payment_intent",
      );
      if (paymentIntent) {
        const stripe = new Stripe(
          env.STRIPE_SECRET_KEY,
        );
        const supabaseAdmin = createSbClientSSR(
          headers,
          request,
          context,
          true,
        );
        await StripeService.statusActiveByIntent(
          stripe,
          paymentIntent,
          supabaseAdmin,
          uid,
        );
      }

      const userPermissionData =
        await UserService.getUserMinePermission(
          supabase,
        );
      let paymentIssue: null | string = null;
      if (
        userPermissionData.subscription_active
      ) {
        const res =
          await StripeService.getAllActiveUserId(
            supabase,
            userData.customer_id,
          );
          console.log('res:', res);
        if (res.length > 0) {
          const crrent = res[0];
          if (
            !(
              crrent.status === "active" ||
              crrent.status === "trialing"
            )
          ) {
            paymentIssue = crrent.status;
          }
        }
      }
      console.log('paymentIssue:', paymentIssue);
      return json({
        ...base,
        userData: {
          ...userData,
        },
        notificationData: notificationData,
        subscription_active:
          userPermissionData.subscription_active,
        paymentIssue,
      });
    } catch (e) {
      return json({
        ...base,
        paymentIssue: null,
      });
    }
  }
  return json({
    ...base,
  });
};

/* export const links: LinksFunction = () => [
  ...(cssBundleHref
    ? [{ rel: "stylesheet", href: cssBundleHref }]
    : [
      ]),
];
 */
export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet }, // <- 新規追加
  // ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []), // <- 既存のコードをコメントアウト
];

function App() {
  const { revalidate } = useRevalidator();
  const data = useLoaderData<typeof loader>();

  useEffect(() => {
    const supabase = createSbClientBrowser();
    supabase.auth.onAuthStateChange(
      (_event, session) => {
        revalidate();
      },
    );
  }, [revalidate]);

  const nav = useNavigation(); // 👈ここ
  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
        />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&family=Oswald:wght@200..700&display=swap"
          rel="stylesheet"
        />
        <script src="https://js.stripe.com/v3/"></script>
        <Meta />
        <Links />
      </head>
      <body>
        {nav.state === "loading" && (
          <ProgressBar />
        )}
        <LazyMotion features={domAnimation}>
          <MotionConfig
            transition={{
              y: easeUse("easeOutQuad", {
                duration: 0.5,
              }),
              opacity: easeUse("easeInOutSine", {
                duration: 0.3,
              }),
            }}
          >
            {data.paymentIssue ? (
              <div
                className={clsx(
                  "fixed",
                  "z-[100]",
                  "border",
                  "border-red-500",
                  "bg-red-100",
                  "text-red-500",
                  "p-4",
                  "w-300",
                  "max-md:w-280",
                  "left-40",
                  "mx-auto",
                  "top-80",
                  "rounded-md",
                  "text-center",
                  "leading-[1.6]",
                  "text-12",
                )}
              >
                <h2>
                  支払いに問題が発生しました
                </h2>
                <div>
                  <Form
                    action="/stripe-portal"
                    method="post"
                  >
                    <button
                      className={clsx(
                        "underline",
                      )}
                    >
                      こちら
                    </button>
                    から支払い情報を
                    <br />
                    確認してください
                  </Form>
                </div>
              </div>
            ) : null}
            <Toaster />
            <AppHeader />
            <Outlet />
            <ScrollRestoration />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
              }}
            />
            <Scripts />
            {/* <LiveReload port={8002} /> */}
            <div id="modal-container" />
            <AppFooter></AppFooter>
          </MotionConfig>
        </LazyMotion>
      </body>
    </html>
  );
}
export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
        />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&family=Oswald:wght@200..700&display=swap"
          rel="stylesheet"
        />
        <script src="https://js.stripe.com/v3/"></script>
        <Meta />
        <Links />
      </head>
      <body>
        <AppHeader />
        <div
          className={clsx(
            "min-h-[80vh]",
            "flex",
            "items-center",
            "justify-center",
          )}
        >
          <div className={clsx()}>
            {isRouteErrorResponse(error) ? (
              <div>
                <h1
                  className={clsx(
                    "text-center",
                    "text-18",
                    "mb-16",
                    "font-bold",
                  )}
                >
                  {error.status}{" "}
                  {error.statusText}
                </h1>
                <div className={clsx()}>
                  {error.status === 404 ? (
                    <div className={clsx()}>
                      <h2
                        className={clsx(
                          "text-center",
                          "text-80",
                          "mb-16",
                        )}
                      >
                        （ ; ; ）
                      </h2>
                      <p
                        className={clsx(
                          "text-center",
                          "text-14",
                          "text-gray-600",
                        )}
                      >
                        お探しのページは見つかりません。
                      </p>
                    </div>
                  ) : (
                    <div className={clsx()}>
                      <h2
                        className={clsx(
                          "text-center",
                          "text-80",
                          "mb-16",
                        )}
                      >
                        （ ; ; ）
                      </h2>
                      <p
                        className={clsx(
                          "text-center",
                          "text-14",
                          "text-gray-600",
                        )}
                      >
                        ご迷惑をおかけして申し訳ありません。
                        <br />
                        時間をおいて再度アクセスして
                        ください。
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h1
                  className={clsx(
                    "text-center",
                    "text-18",
                    "mb-16",
                    "font-bold",
                  )}
                >
                  問題が発生しました
                </h1>
                <h2
                  className={clsx(
                    "text-center",
                    "text-80",
                    "mb-16",
                  )}
                >
                  （ ; ; ）
                </h2>
                <p
                  className={clsx(
                    "text-center",
                    "text-14",
                    "text-gray-600",
                  )}
                >
                  ご迷惑をおかけして申し訳ありません。
                  <br />
                  時間をおいて再度アクセスして
                  ください。
                </p>
              </div>
            )}
          </div>
        </div>
        <AppFooter></AppFooter>
      </body>
    </html>
  );
}
